import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, User } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";

const LOGO_PRINCIPAL = "/maternnia-logo-dark.png";

const NAV_LINKS = [
  { label: "Início", to: "/", id: "inicio" },
  { label: "Etapas", href: "/#etapas", id: "etapas" },
  { label: "Profissionais", to: "/profissionais", id: "profissionais" },
  { label: "Conteúdos", to: "/conteudos", id: "conteudos" },
  { label: "Para fornecedores", to: "/fornecedores", id: "fornecedores" },
  { label: "Sobre", href: "/#sobre", id: "sobre" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const contaHref = user ? (user.role === "fornecedor" ? "/painel-fornecedor" : "/minha-conta") : "/entrar";
  const contaLabel = user ? "Minha conta" : "Entrar";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 backdrop-blur-xl transition-all duration-300 ${
        scrolled ? "bg-white/85 border-b border-maternnia-nevoa shadow-[0_4px_24px_rgba(43,37,38,0.04)]" : "bg-white/60 border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20 md:h-24">
          <Link to="/" data-testid="logo-link" className="flex items-center group">
            <img
              src={LOGO_PRINCIPAL}
              alt="Maternnia"
              className="h-12 md:h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.04]"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV_LINKS.map((link) =>
              link.to ? (
                <Link
                  key={link.id}
                  to={link.to}
                  data-testid={`nav-${link.id}-link`}
                  className="relative text-sm font-medium text-maternnia-charcoal hover:text-maternnia-eucalipto transition-colors duration-300 after:absolute after:left-0 after:-bottom-1.5 after:h-px after:w-0 after:bg-maternnia-eucalipto after:transition-all after:duration-300 hover:after:w-full"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.id}
                  href={link.href}
                  data-testid={`nav-${link.id}-link`}
                  className="relative text-sm font-medium text-maternnia-charcoal hover:text-maternnia-eucalipto transition-colors duration-300 after:absolute after:left-0 after:-bottom-1.5 after:h-px after:w-0 after:bg-maternnia-eucalipto after:transition-all after:duration-300 hover:after:w-full"
                >
                  {link.label}
                </a>
              )
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            <Link
              to={contaHref}
              data-testid="header-account-link"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-maternnia-charcoal hover:text-maternnia-eucalipto transition-colors duration-300"
            >
              <User size={16} /> {contaLabel}
            </Link>
            <Link
              to="/familias"
              data-testid="header-signup-cta"
              className="inline-flex items-center bg-maternnia-eucalipto text-white text-sm font-medium hover:bg-maternnia-floresta rounded-full px-6 py-2.5 transition-all duration-300 shadow-sm hover:shadow-md hover:scale-[1.04] active:scale-[0.97]"
            >
              Cadastre-se gratuitamente
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-maternnia-charcoal p-2 rounded-full transition-colors duration-300 hover:bg-maternnia-nevoa/50 active:scale-90 active:bg-maternnia-nevoa"
            aria-label="Abrir menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="lg:hidden overflow-hidden bg-white border-t border-maternnia-nevoa"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="px-6 py-6 space-y-1">
              {NAV_LINKS.map((link) => {
                const cls = "block py-3 px-3 -mx-3 rounded-xl text-base font-medium text-maternnia-charcoal hover:text-maternnia-eucalipto hover:bg-maternnia-nevoa/40 active:bg-maternnia-nevoa transition-colors duration-300";
                return link.to ? (
                  <Link key={link.id} to={link.to} data-testid={`mobile-nav-${link.id}-link`} onClick={() => setOpen(false)} className={cls}>
                    {link.label}
                  </Link>
                ) : (
                  <a key={link.id} href={link.href} data-testid={`mobile-nav-${link.id}-link`} onClick={() => setOpen(false)} className={cls}>
                    {link.label}
                  </a>
                );
              })}
              <Link
                to={contaHref}
                data-testid="mobile-account-link"
                onClick={() => setOpen(false)}
                className="mt-4 flex items-center justify-center gap-2 border border-maternnia-nevoa text-maternnia-charcoal text-sm font-medium rounded-full px-6 py-3 transition-all duration-300 active:scale-[0.97]"
              >
                <User size={16} /> {contaLabel}
              </Link>
              <Link
                to="/familias"
                data-testid="mobile-signup-cta"
                onClick={() => setOpen(false)}
                className="mt-3 block text-center bg-maternnia-eucalipto text-white text-sm font-medium rounded-full px-6 py-3.5 transition-all duration-300 active:scale-[0.97]"
              >
                Cadastre-se gratuitamente
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
